import { useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, ChevronDown, History, LogOut, PlusCircle, RotateCcw } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Eintraege } from "./Eintraege"
import { NeueMischung, type MischungKopf } from "./NeueMischung"
import type { Hinweis } from "./TerminalApp"
import { Feld, Kachel } from "@/components/terminal/Feld"
import { ALLE_FELDER, BEMERKUNGEN, LABOR_ANF, LABOR_GRAED, REIHENFOLGE, SOLL, WEITERE, ZENTRIFUGE, type FeldDef } from "@/components/terminal/felder"
import { useChipLeser, useLeerlauf } from "@/components/terminal/hooks"
import { Tastenfeld, tasteAnwenden, type TastenModus } from "@/components/terminal/Tastenfeld"
import { cn, nf0 } from "@/lib/format"
import { alsMinSek, alsText, ampelAnfeuchtung, ampelGraedigkeit, grenzwertHinweise, minSek, zahlAus, type Ampel } from "@/lib/pruefung"
import { antwortDetails, terminalApi, type ProbeAntwort, type TerminalStatus, type Vorauswahl, type Werte } from "@/lib/terminalApi"

type Ansicht = "start" | "neu" | "formular" | "eintraege" | "erfolg"
type Texte = Record<string, string>

const KOPF_FELDER = ["mischung", "chargen", "nc_typ", "gebinde", "medium"] as const

interface Props {
  status: TerminalStatus
  name: string
  sitzungSekunden: number
  onAbmelden: (hinweis?: Hinweis) => void
}

/** Werte vom Server (Zahlen, Sekunden) → Texte im Formular ("35,6", "8:14"). */
function alsTexte(werte: Werte | null | undefined): Texte {
  const t: Texte = {}
  if (!werte) return t
  for (const [k, v] of Object.entries(werte)) {
    if (v == null) continue
    if (k === "zykluszeit_s") t.zykluszeit = alsMinSek(Number(v))
    else t[k] = typeof v === "number" ? alsText(v) : String(v)
  }
  return t
}

/** Formular → Nutzlast für die API. */
function nutzlast(t: Texte): Record<string, unknown> {
  const p: Record<string, unknown> = {}
  for (const f of ALLE_FELDER) {
    const v = t[f.key]
    if (f.key === "zykluszeit") p.zykluszeit_s = v ? minSek(v) : null
    else if (f.modus === "text") p[f.key] = v?.trim() ? v.trim() : null
    else if (f.modus === "ganz") p[f.key] = v ? parseInt(v, 10) : null
    else p[f.key] = zahlAus(v)
  }
  for (const k of KOPF_FELDER) p[k] = t[k]?.trim() || null
  return p
}

export function Erfassung({ status, name, sitzungSekunden, onAbmelden }: Props) {
  const qc = useQueryClient()
  const [ansicht, setAnsicht] = useState<Ansicht>("start")
  const [texte, setTexte] = useState<Texte>({})
  const [basis, setBasis] = useState<Texte>({})
  const [aktiv, setAktiv] = useState<string | null>(null)
  const [fenster, setFenster] = useState(status.jetzt.zeitfenster)
  const [bemerkungen, setBemerkungen] = useState<string[]>([])
  const [freitext, setFreitext] = useState("")
  const [weitereOffen, setWeitereOffen] = useState(false)
  const [rueckfrage, setRueckfrage] = useState<string[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [erfolg, setErfolg] = useState<ProbeAntwort | null>(null)
  const [bearbeite, setBearbeite] = useState<ProbeAntwort | null>(null)
  const [chipHinweis, setChipHinweis] = useState(false)

  const sitzungWeg = useCallback((e: unknown) => {
    const d = antwortDetails(e)
    if (d.status === 401) {
      onAbmelden({ text: d.detail, ton: "fehler" })
      return true
    }
    return false
  }, [onAbmelden])

  const vorauswahl = useQuery({
    queryKey: ["terminal-vorauswahl"],
    queryFn: async () => (await terminalApi.get<Vorauswahl>("/vorauswahl")).data,
    retry: false,
  })
  useEffect(() => {
    if (vorauswahl.error) sitzungWeg(vorauswahl.error)
  }, [vorauswahl.error, sitzungWeg])
  const v = vorauswahl.data
  const zuletzt = v?.zuletzt ?? null

  const rest = useLeerlauf(sitzungSekunden, () => onAbmelden({ text: "Wegen Inaktivität abgemeldet. Nicht gespeicherte Werte wurden verworfen.", ton: "info" }), ansicht !== "erfolg")

  // Physische Tastatur und RFID-Leser: Eingaben vor einem Chip-„Tippen“ wiederherstellen
  const texteRef = useRef(texte)
  texteRef.current = texte
  const vorBurst = useRef<Texte>({})
  const letzteTaste = useRef(0)
  useEffect(() => {
    const merken = (e: KeyboardEvent) => {
      const jetzt = performance.now()
      if (jetzt - letzteTaste.current > 60) vorBurst.current = texteRef.current
      letzteTaste.current = jetzt
      void e
    }
    window.addEventListener("keydown", merken, true)
    return () => window.removeEventListener("keydown", merken, true)
  }, [])
  useChipLeser(() => {
    setTexte(vorBurst.current)
    setChipHinweis(true)
  }, true)

  const feldDef = useMemo(() => Object.fromEntries(ALLE_FELDER.map((f) => [f.key, f])) as Record<string, FeldDef>, [])
  const modus: TastenModus = aktiv && feldDef[aktiv] && feldDef[aktiv].modus !== "text" ? (feldDef[aktiv].modus as TastenModus) : "zahl"

  function weiter(von: string | null) {
    const i = REIHENFOLGE.findIndex((f) => f.key === von)
    const naechstes = REIHENFOLGE.slice(i + 1).find((f) => !texteRef.current[f.key])
    setAktiv(naechstes ? naechstes.key : null)
  }

  function taste(t: string) {
    if (!aktiv) return
    const def = feldDef[aktiv]
    if (!def || def.modus === "text") return
    const neu = tasteAnwenden(texte[aktiv] ?? "", t, def.modus)
    setTexte({ ...texte, [aktiv]: neu })
    if (def.autoWeiter && /,\d$/.test(neu)) weiter(aktiv)
  }

  // Physische Zahlentastatur in Zahlenfeldern
  useEffect(() => {
    const taste_ = (e: KeyboardEvent) => {
      if (!aktiv || ansicht !== "formular") return
      const def = feldDef[aktiv]
      if (!def || def.modus === "text") return
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "TEXTAREA") return
      let k = e.key
      if (k === "." ) k = def.modus === "minsek" ? ":" : ","
      if (k === "Backspace") k = "⌫"
      if (k === "Enter" || k === "Tab") {
        e.preventDefault()
        weiter(aktiv)
        return
      }
      if (/^[0-9,:]$/.test(k) || k === "⌫") {
        e.preventDefault()
        const neu = tasteAnwenden(texteRef.current[aktiv] ?? "", k, def.modus)
        setTexte((t) => ({ ...t, [aktiv]: neu }))
      }
    }
    window.addEventListener("keydown", taste_)
    return () => window.removeEventListener("keydown", taste_)
  })

  function formularStarten(werte: Texte, aktivFeld = "anf_ist_1") {
    setTexte(werte)
    setBasis(werte)
    setBemerkungen([])
    setFreitext("")
    setFehler(null)
    setWeitereOffen(false)
    setAktiv(aktivFeld)
    setAnsicht("formular")
  }

  function weiterWieZuletzt() {
    if (!zuletzt) return
    const t = alsTexte(zuletzt)
    // Mess- und Laborwerte nicht übernehmen; Zykluszeit und Auswurf per „wie zuletzt“
    for (const k of ["zykluszeit", "auswurfgewicht_kg", "datum", "zeitfenster"]) delete t[k]
    setBearbeite(null)
    formularStarten(t)
  }

  async function neueMischung(kopf: MischungKopf) {
    setFehler(null)
    try {
      const r = await terminalApi.get<{ gefunden: boolean; werte: Werte }>("/fahrweise", {
        params: { nc_typ: kopf.nc_typ, gebinde: kopf.gebinde, medium: kopf.medium },
      })
      const t = { ...alsTexte(r.data.werte), ...kopf }
      setBearbeite(null)
      formularStarten(t)
    } catch (e) {
      if (!sitzungWeg(e)) setFehler(antwortDetails(e).detail)
    }
  }

  function bearbeiten(p: ProbeAntwort) {
    const t = alsTexte(p)
    setBearbeite(p)
    setFenster(p.zeitfenster ?? status.jetzt.zeitfenster)
    formularStarten(t, "anf_ist_1")
    const vorhanden = BEMERKUNGEN.filter((b) => (p.bemerkung as string | null)?.includes(b.text)).map((b) => b.text)
    setBemerkungen(vorhanden)
    let rest_ = (p.bemerkung as string | null) ?? ""
    for (const b of vorhanden) rest_ = rest_.replace(b, "")
    setFreitext(rest_.replace(/^[\s·]+|[\s·]+$/g, ""))
  }

  async function speichern(bestaetigt = false) {
    setLaeuft(true)
    setFehler(null)
    const testgemisch = BEMERKUNGEN.find((b) => bemerkungen.includes(b.text) && "testgemisch" in b)
    const bemerkung = [...bemerkungen, freitext.trim()].filter(Boolean).join(" · ") || null
    const daten = {
      ...nutzlast(texte),
      zeitfenster: fenster,
      bemerkung,
      testgemisch: testgemisch && "testgemisch" in testgemisch ? testgemisch.testgemisch : null,
      bestaetigt,
    }
    try {
      const r = bearbeite
        ? await terminalApi.put<ProbeAntwort>(`/proben/${bearbeite.id}`, daten)
        : await terminalApi.post<ProbeAntwort>("/proben", daten)
      setRueckfrage(null)
      setErfolg(r.data)
      setAnsicht("erfolg")
      void qc.invalidateQueries({ queryKey: ["terminal-vorauswahl"] })
      void qc.invalidateQueries({ queryKey: ["terminal-proben"] })
    } catch (e) {
      if (sitzungWeg(e)) return
      const d = antwortDetails(e)
      if (d.status === 409 && d.warnungen) setRueckfrage(d.warnungen)
      else setFehler(d.detail)
    } finally {
      setLaeuft(false)
    }
  }

  // Live-Werte
  const anfSoll = zahlAus(texte.anf_soll)
  const graedSoll = zahlAus(texte.graed_soll)
  const zyklus = texte.zykluszeit ? minSek(texte.zykluszeit) : null
  const auswurf = zahlAus(texte.auswurfgewicht_kg)
  const fuell = zahlAus(texte.fuellgewicht_kg)
  const durchsatz = zyklus && auswurf ? (auswurf / zyklus) * 3600 : null
  // Hinweise erst nach Verlassen des Feldes — sonst warnt schon die erste Ziffer („1 kg“)
  const hinweise = v
    ? grenzwertHinweise(
        aktiv === "zykluszeit" ? null : zyklus,
        aktiv === "auswurfgewicht_kg" ? null : auswurf,
        aktiv === "fuellgewicht_kg" ? null : fuell,
        v.grenzen,
      )
    : []
  const ampel = (key: string): Ampel => {
    if (key.startsWith("anf_ist")) return ampelAnfeuchtung(zahlAus(texte[key]), anfSoll)
    if (key.startsWith("graed_ist")) return ampelGraedigkeit(zahlAus(texte[key]), graedSoll)
    return null
  }
  const feld = (f: FeldDef, extra?: { gross?: boolean; platzhalter?: string }) => (
    <Feld key={f.key} label={f.label} einheit={f.einheit} wert={texte[f.key] ?? ""} aktiv={aktiv === f.key}
      onWaehlen={() => setAktiv(f.key)} ampel={ampel(f.key)} geaendert={!!basis[f.key] && basis[f.key] !== texte[f.key]}
      gross={extra?.gross} platzhalter={extra?.platzhalter} />
  )
  const textFeld = (f: FeldDef) => (
    <label key={f.key} className="grid min-h-[76px] content-between rounded-xl border-2 border-hair/10 bg-surface px-3 py-2 text-[13px] text-ink2">
      {f.label}
      <input value={texte[f.key] ?? ""} onFocus={() => setAktiv(null)} onChange={(e) => setTexte({ ...texte, [f.key]: e.target.value })}
        className="w-full bg-transparent text-xl font-semibold text-ink focus:outline-none" autoComplete="off" />
    </label>
  )
  const zuletztKurz = zuletzt
    ? [zuletzt.mischung, zuletzt.nc_typ, zuletzt.gebinde, zuletzt.medium].filter(Boolean).join(" · ")
    : null

  return (
    <div className="flex min-h-screen flex-col bg-page">
      {/* Kopf: wer, wann, Abmelden */}
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-hair/10 bg-surface px-5 py-3">
        <div className="text-lg"><span className="text-ink2">Angemeldet:</span> <b className="font-semibold">{name}</b></div>
        <div className="rounded-full bg-surface2 px-3 py-1 text-sm">{`${status.jetzt.schicht}schicht · ${new Date(status.jetzt.datum).toLocaleDateString("de-DE")}`}</div>
        {rest <= 20 && (
          <div role="status" className="rounded-full bg-warn/30 px-3 py-1 text-sm font-medium">{`Abmeldung in ${rest} s — tippen zum Bleiben`}</div>
        )}
        <div className="ml-auto flex items-center gap-2">
          {ansicht !== "eintraege" && (
            <button type="button" onClick={() => setAnsicht("eintraege")} className="inline-flex h-12 items-center gap-2 rounded-xl px-4 text-base text-ink2 hover:bg-surface2">
              <History className="h-5 w-5" aria-hidden="true" /> Letzte Einträge
            </button>
          )}
          <button type="button" onClick={() => onAbmelden()} className="inline-flex h-12 items-center gap-2 rounded-xl border-2 border-hair/10 px-4 text-base font-medium">
            <LogOut className="h-5 w-5" aria-hidden="true" /> Abmelden
          </button>
        </div>
      </header>

      {chipHinweis && (
        <div role="alert" className="flex items-center justify-between gap-4 bg-warn/25 px-5 py-3 text-base">
          Anderer Chip erkannt. Zum Wechseln bitte zuerst abmelden.
          <button type="button" onClick={() => setChipHinweis(false)} className="rounded-lg px-3 py-1 underline">OK</button>
        </div>
      )}

      {/* Fensterleiste der laufenden Schicht */}
      {v && ansicht !== "eintraege" && ansicht !== "erfolg" && (
        <nav aria-label="Zeitfenster" className="flex flex-wrap gap-2 px-5 pt-4">
          {v.schicht_fenster.map((f) => (
            <button key={f.zeitfenster} type="button" onClick={() => setFenster(f.zeitfenster)} aria-pressed={fenster === f.zeitfenster}
              className={cn("inline-flex h-12 items-center gap-2 rounded-xl border-2 px-4 text-base font-medium",
                fenster === f.zeitfenster ? "border-accent bg-accent/15 text-accent-strong" : "border-hair/10 bg-surface text-ink2")}>
              {f.erfasst ? <CheckCircle2 className="h-5 w-5 text-good" aria-label="erfasst" /> : f.faellig ? <span className="h-2.5 w-2.5 rounded-full bg-warn" aria-label="fällig" /> : <span className="h-2.5 w-2.5 rounded-full border border-axis" aria-label="später" />}
              {`${f.zeitfenster} Uhr`}
            </button>
          ))}
        </nav>
      )}

      <main className="flex-1 px-5 pb-8 pt-4">
        {fehler && <p role="alert" className="mb-4 rounded-xl bg-crit/10 p-4 text-lg">{fehler}</p>}

        {ansicht === "start" && (
          <div className="grid max-w-5xl gap-4 md:grid-cols-2">
            <button type="button" disabled={!zuletzt} onClick={weiterWieZuletzt}
              className="grid min-h-[200px] content-start gap-3 rounded-2xl border-2 border-accent bg-surface p-6 text-left disabled:border-hair/10 disabled:opacity-50">
              <RotateCcw className="h-9 w-9 text-accent-strong" aria-hidden="true" />
              <span className="text-2xl font-semibold">Weiter wie zuletzt</span>
              <span className="text-lg text-ink2">{zuletztKurz ?? "Noch keine Probe an dieser Anlage."}</span>
              {zuletzt && (
                <span className="text-base text-muted">{`Soll ${alsText(zuletzt.anf_soll as number)} % / ${alsText(zuletzt.graed_soll as number)} % · letzte Probe ${new Date(zuletzt.datum).toLocaleDateString("de-DE")} ${zuletzt.zeitfenster ?? ""}`}</span>
              )}
            </button>
            <button type="button" onClick={() => { setBearbeite(null); setTexte({}); setAnsicht("neu") }}
              className="grid min-h-[200px] content-start gap-3 rounded-2xl border-2 border-hair/10 bg-surface p-6 text-left">
              <PlusCircle className="h-9 w-9 text-accent-strong" aria-hidden="true" />
              <span className="text-2xl font-semibold">Neue Mischung</span>
              <span className="text-lg text-ink2">{`Vorschlag: ${v?.mischung_vorschlag ?? "–"}`}</span>
              <span className="text-base text-muted">NC-Typ, Gebinde und Medium wählen — die übrigen Werte kommen aus der letzten gleichen Fahrweise.</span>
            </button>
          </div>
        )}

        {ansicht === "neu" && v && (
          <NeueMischung vorauswahl={v} start={{ mischung: texte.mischung, chargen: texte.chargen, nc_typ: texte.nc_typ, gebinde: texte.gebinde, medium: texte.medium }}
            onWeiter={(k) => void neueMischung(k)} onZurueck={() => setAnsicht(texte.nc_typ ? "formular" : "start")} />
        )}

        {ansicht === "formular" && v && (
          // Unterhalb von lg liegt das Tastenfeld fest unten: Platz zum Hochscrollen lassen
          <div className={cn("grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:pb-0", aktiv && feldDef[aktiv]?.modus !== "text" && "pb-[400px]")}>
            <div className="grid min-w-0 content-start gap-5">
              {/* Was läuft */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border-2 border-hair/10 bg-surface px-5 py-3">
                <div className="text-xl font-semibold"><span className="font-mono">{texte.mischung}</span> · <span className="font-mono">{texte.nc_typ}</span> · {texte.gebinde} · {texte.medium}</div>
                {bearbeite && <span className="rounded-full bg-warn/30 px-3 py-1 text-sm">Korrektur einer gespeicherten Probe</span>}
                {!bearbeite && (
                  <button type="button" onClick={() => setAnsicht("neu")} className="ml-auto h-11 rounded-xl px-4 text-base text-accent-strong underline underline-offset-4">Mischung ändern</button>
                )}
              </div>

              <section className="grid gap-2">
                <h2 className="flex items-baseline gap-3 text-lg font-semibold">Anfeuchtung Ist <span className="text-sm font-normal text-ink2">{`Soll ${texte.anf_soll ?? "–"} %`}</span></h2>
                <div className="grid grid-cols-3 gap-2">{LABOR_ANF.map((f) => feld(f))}</div>
              </section>
              <section className="grid gap-2">
                <h2 className="flex items-baseline gap-3 text-lg font-semibold">Grädigkeit Ist <span className="text-sm font-normal text-ink2">{`Soll ${texte.graed_soll ?? "–"} %`}</span></h2>
                <div className="grid grid-cols-3 gap-2">{LABOR_GRAED.map((f) => feld(f))}</div>
              </section>
              <section className="grid gap-2">
                <h2 className="text-lg font-semibold">Zentrifuge</h2>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {ZENTRIFUGE.map((f) => feld(f, { gross: true }))}
                </div>
                {zuletzt && (
                  <div className="flex flex-wrap gap-2">
                    {zuletzt.zykluszeit_s != null && (
                      <button type="button" onClick={() => { setTexte({ ...texte, zykluszeit: alsMinSek(zuletzt.zykluszeit_s as number) }); setAktiv("auswurfgewicht_kg") }}
                        className="h-11 rounded-xl border-2 border-hair/10 bg-surface px-4 text-base">{`Zykluszeit wie zuletzt: ${alsMinSek(zuletzt.zykluszeit_s as number)}`}</button>
                    )}
                    {zuletzt.auswurfgewicht_kg != null && (
                      <button type="button" onClick={() => { setTexte({ ...texte, auswurfgewicht_kg: alsText(zuletzt.auswurfgewicht_kg as number) }); setAktiv(null) }}
                        className="h-11 rounded-xl border-2 border-hair/10 bg-surface px-4 text-base">{`Auswurf wie zuletzt: ${alsText(zuletzt.auswurfgewicht_kg as number)} kg`}</button>
                    )}
                  </div>
                )}
              </section>

              <section className="grid gap-2">
                <button type="button" onClick={() => setWeitereOffen(!weitereOffen)} aria-expanded={weitereOffen}
                  className="inline-flex h-12 items-center gap-2 justify-self-start rounded-xl px-1 text-lg font-semibold">
                  <ChevronDown className={cn("h-5 w-5 transition-transform", weitereOffen && "rotate-180")} aria-hidden="true" />
                  Soll-Werte, Zentrifugenparameter, Alkohol
                  <span className="text-sm font-normal text-ink2">(übernommen — nur bei Änderung anfassen)</span>
                </button>
                {weitereOffen && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                    {[...SOLL, ...WEITERE].map((f) => (f.modus === "text" ? textFeld(f) : feld(f)))}
                  </div>
                )}
              </section>

              <section className="grid gap-2">
                <h2 className="text-lg font-semibold">Bemerkung</h2>
                <div className="flex flex-wrap gap-2">
                  {BEMERKUNGEN.map((b) => (
                    <Kachel key={b.text} klein an={bemerkungen.includes(b.text)} onClick={() => {
                      const ohneTest = "testgemisch" in b ? bemerkungen.filter((x) => !x.startsWith("Testgemisch")) : bemerkungen
                      setBemerkungen(bemerkungen.includes(b.text) ? bemerkungen.filter((x) => x !== b.text) : [...ohneTest, b.text])
                    }}>{b.text}</Kachel>
                  ))}
                </div>
                <textarea value={freitext} onChange={(e) => setFreitext(e.target.value)} onFocus={() => setAktiv(null)} rows={2}
                  placeholder="Weitere Bemerkung (optional)"
                  className="w-full rounded-xl border-2 border-hair/10 bg-surface px-4 py-3 text-lg text-ink focus:border-accent focus:outline-none" />
              </section>
            </div>

            {/* Rechte Spalte: Live-Ergebnis, Tastenfeld, Speichern */}
            <aside className="grid content-start gap-4 lg:sticky lg:top-[84px] lg:self-start">
              <div className="rounded-2xl border-2 border-hair/10 bg-surface p-4">
                <div className="text-sm text-ink2">Durchsatz dieser Probe</div>
                <div className="text-4xl font-semibold tabular">{durchsatz ? `${nf0.format(durchsatz)}` : "–"}<span className="ml-1 text-lg font-medium text-ink2">kg/h</span></div>
              </div>
              <div className="hidden lg:block">
                <Tastenfeld modus={modus} onTaste={taste} onWeiter={() => weiter(aktiv)} weiterText={aktiv ? "Weiter" : "Feld wählen"} />
              </div>
              <button type="button" disabled={laeuft} onClick={() => void speichern(false)}
                className="h-20 rounded-2xl bg-good text-2xl font-semibold text-white disabled:opacity-50">
                {laeuft ? "Speichert …" : bearbeite ? "Korrektur speichern" : "Probe speichern"}
              </button>
              {/* Unter dem Speichern-Knopf, damit das Tastenfeld beim Tippen nicht springt */}
              {hinweise.map((h) => <p key={h} role="status" className="m-0 rounded-lg bg-crit/10 px-3 py-2 text-sm">{h}</p>)}
              <button type="button" onClick={() => { setAnsicht("start"); setBearbeite(null) }} className="h-12 rounded-xl text-base text-ink2 underline underline-offset-4">
                Verwerfen
              </button>
            </aside>
            {/* Tastenfeld unten auf schmalen Geräten */}
            {aktiv && feldDef[aktiv]?.modus !== "text" && (
              <div className="fixed inset-x-0 bottom-0 z-30 border-t border-hair/10 bg-page p-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden">
                <Tastenfeld modus={modus} onTaste={taste} onWeiter={() => weiter(aktiv)} />
              </div>
            )}
          </div>
        )}

        {ansicht === "erfolg" && erfolg && (
          <Erfolg probe={erfolg} onWeitere={() => { setErfolg(null); setBearbeite(null); setAnsicht("start") }}
            onAbmelden={() => onAbmelden({ text: `Probe ${erfolg.zeitfenster ?? ""} Uhr gespeichert. Danke!`, ton: "info" })} />
        )}

        {ansicht === "eintraege" && (
          <Eintraege onZurueck={() => setAnsicht("start")} onBearbeiten={bearbeiten} onSitzungWeg={sitzungWeg} />
        )}
      </main>

      {rueckfrage && (
        <div role="dialog" aria-modal="true" aria-labelledby="rueckfrage-titel" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
          <div className="grid w-full max-w-lg gap-4 rounded-2xl bg-surface p-6">
            <h2 id="rueckfrage-titel" className="text-2xl font-semibold">Bitte prüfen</h2>
            <ul className="m-0 grid list-disc gap-2 pl-6 text-lg">{rueckfrage.map((w) => <li key={w}>{w}</li>)}</ul>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setRueckfrage(null)} className="h-16 rounded-xl border-2 border-hair/10 text-lg font-semibold">Korrigieren</button>
              <button type="button" onClick={() => void speichern(true)} className="h-16 rounded-xl bg-crit text-lg font-semibold text-white">Trotzdem speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Erfolg({ probe, onWeitere, onAbmelden }: { probe: ProbeAntwort; onWeitere: () => void; onAbmelden: () => void }) {
  const [rest, setRest] = useState(8)
  const abmelden = useRef(onAbmelden)
  abmelden.current = onAbmelden
  useEffect(() => {
    const t = window.setInterval(() => setRest((r) => r - 1), 1000)
    return () => window.clearInterval(t)
  }, [])
  useEffect(() => {
    if (rest <= 0) abmelden.current()
  }, [rest])
  return (
    <div className="mx-auto grid max-w-xl justify-items-center gap-5 py-10 text-center">
      <CheckCircle2 className="h-24 w-24 text-good" aria-hidden="true" />
      <h2 className="text-3xl font-semibold">Gespeichert</h2>
      <p className="text-xl text-ink2">
        {`${probe.zeitfenster ?? ""} Uhr · ${probe.nc_typ ?? ""} · Durchsatz `}
        <b className="text-ink">{probe.durchsatz_kg_h != null ? `${nf0.format(probe.durchsatz_kg_h)} kg/h` : "–"}</b>
      </p>
      {probe.pruefung !== "ok" && probe.pruefung !== "ausreisser" && (
        <p className="rounded-xl bg-warn/25 px-4 py-2">Die Probe zählt wegen fehlender oder ungewöhnlicher Werte nicht im Durchsatz.</p>
      )}
      <div className="grid w-full grid-cols-2 gap-3">
        <button type="button" onClick={onWeitere} className="h-16 rounded-xl border-2 border-hair/10 bg-surface text-lg font-semibold">Weitere Probe</button>
        <button type="button" onClick={onAbmelden} className="h-16 rounded-xl bg-accent-strong text-lg font-semibold text-white">{`Abmelden (${Math.max(0, rest)})`}</button>
      </div>
    </div>
  )
}
